import { useState, useCallback, useRef, useEffect } from 'react';
import superjson from 'superjson';

// Available Inworld voices - expanded list
export const INWORLD_VOICES = [
  // Original voices
  { id: 'Dennis', name: 'Dennis', gender: 'male', style: 'professional' },
  { id: 'Sarah', name: 'Sarah', gender: 'female', style: 'friendly' },
  { id: 'James', name: 'James', gender: 'male', style: 'authoritative' },
  { id: 'Emma', name: 'Emma', gender: 'female', style: 'warm' },
  { id: 'Michael', name: 'Michael', gender: 'male', style: 'casual' },
  { id: 'Olivia', name: 'Olivia', gender: 'female', style: 'professional' },
  { id: 'David', name: 'David', gender: 'male', style: 'friendly' },
  { id: 'Sophie', name: 'Sophie', gender: 'female', style: 'energetic' },
  // Additional voices
  { id: 'Rafael', name: 'Rafael', gender: 'male', style: 'warm' },
  { id: 'Omar', name: 'Omar', gender: 'male', style: 'confident' },
  { id: 'Clive', name: 'Clive', gender: 'male', style: 'distinguished' },
  { id: 'Luna', name: 'Luna', gender: 'female', style: 'gentle' },
  { id: 'Ashley', name: 'Ashley', gender: 'female', style: 'upbeat' },
  { id: 'Deborah', name: 'Deborah', gender: 'female', style: 'mature' },
  { id: 'Wendy', name: 'Wendy', gender: 'female', style: 'cheerful' },
  { id: 'Timothy', name: 'Timothy', gender: 'male', style: 'thoughtful' },
] as const;

export type InworldVoiceId = (typeof INWORLD_VOICES)[number]['id'];

interface TTSOptions {
  voiceId?: InworldVoiceId;
  speakingRate?: number;
  temperature?: number;
}

interface UseInworldTTSReturn {
  speak: (text: string, options?: TTSOptions) => Promise<void>;
  stop: () => void;
  isPlaying: boolean;
  isSpeaking: boolean;
  error: string | null;
}

// Global reference to track the active TTS instance
// This prevents multiple TTS systems from playing simultaneously
let activeInworldTTSInstance: { stop: () => void } | null = null;

/**
 * Stop any globally active Inworld TTS instance
 * Call this when another TTS system (like OpenAI) starts playing
 */
export function stopAllInworldTTS() {
  if (activeInworldTTSInstance) {
    activeInworldTTSInstance.stop();
    activeInworldTTSInstance = null;
  }
}

/**
 * Hook for Inworld Text-to-Speech
 * Uses streaming API for real-time audio playback
 * Implements global instance tracking to prevent audio conflicts
 */
// Global cooldown: after a TTS API error, skip all speak() calls for this period
let ttsErrorCooldownUntil = 0;
const TTS_ERROR_COOLDOWN_MS = 30_000; // 30 seconds after a transient error
const TTS_AUTH_ERROR_COOLDOWN_MS = 5 * 60_000; // 5 minutes after a 403/401 auth error
// Global in-flight guard: prevents concurrent speak() calls across all instances
let ttsInFlight = false;

export function useInworldTTS(): UseInworldTTSReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const instanceIdRef = useRef<string>(Math.random().toString(36));

  const stop = useCallback(() => {
    // Abort any ongoing fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Stop audio playback
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop();
      } catch (e) {
        // Ignore if already stopped
      }
      sourceNodeRef.current = null;
    }

    setIsPlaying(false);
    setIsSpeaking(false);

    // Clear global reference if this was the active instance
    if (activeInworldTTSInstance?.stop === stop) {
      activeInworldTTSInstance = null;
    }
  }, []);

  // Register this instance on mount
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      stop();
    };
  }, [stop]);

  const speak = useCallback(
    async (text: string, options: TTSOptions = {}) => {
      // Skip if we're in a cooldown period after a recent API error
      if (Date.now() < ttsErrorCooldownUntil) return;

      // Prevent concurrent speak() calls globally (race condition guard)
      if (ttsInFlight) return;
      ttsInFlight = true;

      // Stop any existing playback globally
      stopAllInworldTTS();

      if (!text.trim()) {
        ttsInFlight = false;
        return;
      }

      setError(null);
      setIsSpeaking(true);

      const { voiceId = 'Dennis', speakingRate = 1, temperature = 1.1 } = options;

      try {
        abortControllerRef.current = new AbortController();

        // Call our backend endpoint which proxies to Inworld
        const response = await fetch('/_api/tts/inworld', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: superjson.stringify({
            text,
            voiceId,
            speakingRate,
            temperature,
          }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const statusCode = response.status;
          // Auth errors (401/403) get a much longer cooldown — these won't self-heal
          if (statusCode === 401 || statusCode === 403) {
            ttsErrorCooldownUntil = Date.now() + TTS_AUTH_ERROR_COOLDOWN_MS;
            console.warn(`[InworldTTS] Auth error ${statusCode} — TTS disabled for 5 minutes`);
          } else {
            ttsErrorCooldownUntil = Date.now() + TTS_ERROR_COOLDOWN_MS;
          }
          ttsInFlight = false;
          // Use a single batched state update to minimize re-renders
          setError(errorData.error || `TTS request failed: ${statusCode}`);
          setIsSpeaking(false);
          return;
        }

        // Get the audio as an array buffer
        const audioData = await response.arrayBuffer();

        // Create audio context if needed
        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }

        const audioContext = audioContextRef.current;

        // Resume context if suspended (needed for autoplay policies)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Decode the audio data
        const audioBuffer = await audioContext.decodeAudioData(audioData);

        // Create and play source node
        const sourceNode = audioContext.createBufferSource();
        sourceNode.buffer = audioBuffer;
        sourceNode.connect(audioContext.destination);

        sourceNode.onended = () => {
          setIsPlaying(false);
          setIsSpeaking(false);
          sourceNodeRef.current = null;
          // Clear global reference
          if (activeInworldTTSInstance?.stop === stop) {
            activeInworldTTSInstance = null;
          }
        };

        // Register as active globally
        activeInworldTTSInstance = { stop };

        sourceNodeRef.current = sourceNode;
        setIsPlaying(true);
        sourceNode.start();
        ttsInFlight = false;
      } catch (err) {
        ttsInFlight = false;
        if (err instanceof Error && err.name === 'AbortError') {
          // User cancelled, not an error
          return;
        }
        console.error('[InworldTTS] Error:', err);
        // Set global cooldown to prevent rapid-fire retries across all hook instances
        ttsErrorCooldownUntil = Date.now() + TTS_ERROR_COOLDOWN_MS;
        setError(err instanceof Error ? err.message : 'TTS failed');
        setIsSpeaking(false);
      }
    },
    [stop],
  );

  return {
    speak,
    stop,
    isPlaying,
    isSpeaking,
    error,
  };
}

/**
 * Get a consistent voice for an AI participant based on their persona
 */
export function getVoiceForPersona(
  personaId: number | string,
  personaName?: string,
): InworldVoiceId {
  // Use persona ID to consistently assign a voice via stable hash
  let hash = 0;
  const str = String(personaId || personaName || 'default');
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  const voiceIndex = Math.abs(hash) % INWORLD_VOICES.length;
  return INWORLD_VOICES[voiceIndex].id;
}

/**
 * Detect emotion from message content and return Inworld emotion markup
 */
function detectEmotion(text: string): string {
  const lower = text.toLowerCase();
  if (/\b(error|fail|crash|broke|bug|issue|problem)\b/.test(lower)) return '[sad]';
  if (/\b(great|awesome|perfect|excellent|nice|wonderful|congrats)\b/.test(lower)) return '[happy]';
  if (/\b(haha|lol|funny|joke|😂|😄)\b/.test(lower)) return '[laughing]';
  if (/\b(sorry|apologize|unfortunately|regret)\b/.test(lower)) return '[sad]';
  if (/\b(warning|caution|careful|danger|critical)\b/.test(lower)) return '[fearful]';
  if (/\b(hmm|interesting|curious|wonder)\b/.test(lower)) return '[curious]';
  return '';
}

/**
 * Clean text for TTS - remove code blocks, markdown, etc.
 * Prepends Inworld emotion markup based on content analysis.
 */
export function cleanTextForTTS(text: string): string {
  const emotion = detectEmotion(text);
  const cleaned = text
    // Remove code blocks
    .replace(/```[\s\S]*?```/g, 'Code block.')
    // Remove inline code
    .replace(/`[^`]+`/g, 'code')
    // Remove markdown headers
    .replace(/^#{1,6}\s+/gm, '')
    // Remove markdown bold/italic
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    // Remove markdown links
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    // Remove markdown images
    .replace(/!\[([^\]]*)\]\([^)]+\)/g, 'Image: $1')
    // Remove HTML tags
    .replace(/<[^>]+>/g, '')
    // Remove special markers
    .replace(/\[THINKING\]|\[REASONING\]|\[PLANNING\]|\[STEPS\]|\[CODE\]/g, '')
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .trim()
    // Limit length for TTS (avoid very long audio)
    .slice(0, 1000);

  return emotion ? `${emotion} ${cleaned}` : cleaned;
}
